import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookIcon, EyeIcon, EyeOffIcon, CheckIcon, StoreIcon, HeadphonesIcon, UserIcon } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
type AccountType = 'author' | 'narrator' | 'vendor';
const CreateVendorAccount = () => {
  const {
    darkMode
  } = useDarkMode();
  const [accountType, setAccountType] = useState<AccountType>('author');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      setIsSuccess(true);
      console.log('Vendor account created:', {
        ...formData,
        accountType
      });
    }, 1500);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const getDisplayNameLabel = () => {
    switch (accountType) {
      case 'author':
        return 'Author Name / Pen Name';
      case 'narrator':
        return 'Narrator Name';
      case 'vendor':
        return 'Store Name';
      default:
        return 'Display Name';
    }
  };
  if (isSuccess) {
    return <div className={`min-h-screen flex items-center justify-center px-4 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`} data-id="element-9154" data-mp="3nnebf9aog3kh">
        <div className={`max-w-md w-full p-8 rounded-lg shadow-lg text-center ${darkMode ? 'bg-gray-800' : 'bg-white'}`} data-id="element-9155" data-mp="3jmkj83q7c2ls">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 mb-4" data-id="element-9156" data-mp="1mn9epkyjkoke">
            <CheckIcon className="h-6 w-6 text-purple-600" data-id="element-9157" data-mp="1rm2rtc9x2dk1" />
          </div>
          <h2 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-9158" data-mp="1wi1acj8kd1cj">
            Business Account Created!
          </h2>
          <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-9159" data-mp="25izk7i5oo331">
            Welcome to LitLaunch. Let's complete your {accountType} profile so
            you can start reaching readers.
          </p>
          <Link to="/vendor/profile/settings" data-id="element-9160" data-mp="2fac8y0w6qd4u">
            <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white" data-id="element-9161" data-mp="2k8nieaikilmj">
              Set Up Your Profile
            </Button>
          </Link>
        </div>
      </div>;
  }
  return <div className={`min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`} data-id="element-9162" data-mp="2t9bx77fscq0h">
      <div className={`max-w-md w-full space-y-8 p-10 rounded-xl shadow-lg ${darkMode ? 'bg-gray-800' : 'bg-white'}`} data-id="element-9163" data-mp="hnt7jgtzp64f">
        <div className="text-center" data-id="element-9164" data-mp="8lk2h6hhq4yf">
          <Link to="/" className="flex items-center justify-center mb-6" data-id="element-9165" data-mp="8mmp327s5l8j">
            <BookIcon className="h-8 w-8 text-purple-600" data-id="element-9166" data-mp="3n0dex7lod1u" />
            <span className={`ml-2 text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-9167" data-mp="3ol8qp1qo00r">
              LitLaunch
            </span>
          </Link>
          <h2 className={`mt-2 text-3xl font-extrabold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-9168" data-mp="wbxvp0bta9ul">
            Create Business Account
          </h2>
          <p className={`mt-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-9169" data-mp="mkb8s6q42mc0">
            Start selling on the leading indie book marketplace
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit} data-id="element-9170" data-mp="1f98i3kcch8sm">
          {/* Account Type Selector */}
          <div data-id="element-9171" data-mp="zqfln76ectjn">
            <label className={`block text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9172" data-mp="zqpfj5ukbkum">
              I am a...
            </label>
            <div className="grid grid-cols-3 gap-3" data-id="element-9173" data-mp="pvdy2frrrzj6">
              <button type="button" onClick={() => setAccountType('author')} className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${accountType === 'author' ? 'border-purple-600 bg-purple-50 text-purple-700' : darkMode ? 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'}`} data-id="element-9174" data-mp="usny0pcsbtl9">
                <BookIcon className={`h-6 w-6 mb-1 ${accountType === 'author' ? 'text-purple-600' : 'text-gray-400'}`} data-id="element-9175" data-mp="1dpp1g5qoxln3" />
                <span className="text-xs font-medium" data-id="element-9176" data-mp="268cfj179s0eo">Author</span>
              </button>

              <button type="button" onClick={() => setAccountType('narrator')} className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${accountType === 'narrator' ? 'border-purple-600 bg-purple-50 text-purple-700' : darkMode ? 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'}`} data-id="element-9177" data-mp="3qxyb7y613gvk">
                <HeadphonesIcon className={`h-6 w-6 mb-1 ${accountType === 'narrator' ? 'text-purple-600' : 'text-gray-400'}`} data-id="element-9178" data-mp="381gpbxdv2b78" />
                <span className="text-xs font-medium" data-id="element-9179" data-mp="3h13fto51xwli">Narrator</span>
              </button>

              <button type="button" onClick={() => setAccountType('vendor')} className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${accountType === 'vendor' ? 'border-purple-600 bg-purple-50 text-purple-700' : darkMode ? 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'}`} data-id="element-9180" data-mp="3c063hs6q3wrx">
                <StoreIcon className={`h-6 w-6 mb-1 ${accountType === 'vendor' ? 'text-purple-600' : 'text-gray-400'}`} data-id="element-9181" data-mp="3u3w9sz23gp0z" />
                <span className="text-xs font-medium" data-id="element-9182" data-mp="2wtkkd5tgste">Vendor</span>
              </button>
            </div>
          </div>

          <div className="space-y-4" data-id="element-9183" data-mp="32anhbrxr0m3t">
            <div data-id="element-9184" data-mp="36dadu65d6v7c">
              <label htmlFor="displayName" className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9185" data-mp="36ev8rwvlcg5q">
                {getDisplayNameLabel()}
              </label>
              <Input id="displayName" name="displayName" required value={formData.displayName} onChange={handleChange} className={darkMode ? 'bg-gray-700 border-gray-600 text-white focus:ring-purple-500' : 'focus:ring-purple-500'} placeholder={accountType === 'vendor' ? 'My Awesome Book Shop' : 'Jane Doe'} data-id="element-9186" data-mp="3g5pyjb81pxvx" />
            </div>

            <div data-id="element-9187" data-mp="223qpoqbg28va">
              <label htmlFor="email" className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9188" data-mp="226cwgnqigkcg">
                Email Address
              </label>
              <Input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} className={darkMode ? 'bg-gray-700 border-gray-600 text-white focus:ring-purple-500' : 'focus:ring-purple-500'} placeholder="business@example.com" data-id="element-9189" data-mp="1nf4cau5dnr6p" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-9190" data-mp="32b5bpb44za2r">
              <div data-id="element-9191" data-mp="329b0wpmkwyce">
                <label htmlFor="password" className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9192" data-mp="2obmh7l48zx8x">
                  Password
                </label>
                <div className="relative" data-id="element-9193" data-mp="2ekrrg6rsmfiq">
                  <Input id="password" name="password" type={showPassword ? 'text' : 'password'} required value={formData.password} onChange={handleChange} className={darkMode ? 'bg-gray-700 border-gray-600 text-white focus:ring-purple-500' : 'focus:ring-purple-500'} data-id="element-9194" data-mp="2ejzy72lo4tvm" />
                  <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500" onClick={() => setShowPassword(!showPassword)} data-id="element-9195" data-mp="1h0007idlzrau">
                    {showPassword ? <EyeOffIcon className="h-4 w-4" data-id="element-9196" data-mp="1lzl3u63q4ntw" /> : <EyeIcon className="h-4 w-4" data-id="element-9197" data-mp="1zz49ms92bn31" />}
                  </button>
                </div>
              </div>

              <div data-id="element-9198" data-mp="1uyzryywf4mb8">
                <label htmlFor="confirmPassword" className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-9199" data-mp="1uzhta9xxtqq0">
                  Confirm Password
                </label>
                <div className="relative" data-id="element-9200" data-mp="2ip2ez0lh722z">
                  <Input id="confirmPassword" name="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} required value={formData.confirmPassword} onChange={handleChange} className={darkMode ? 'bg-gray-700 border-gray-600 text-white focus:ring-purple-500' : 'focus:ring-purple-500'} data-id="element-9201" data-mp="2iq52qzni4oeo" />
                  <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500" onClick={() => setShowConfirmPassword(!showConfirmPassword)} data-id="element-9202" data-mp="p5mvw52n2hnq">
                    {showConfirmPassword ? <EyeOffIcon className="h-4 w-4" data-id="element-9203" data-mp="a7m5trofz01i" /> : <EyeIcon className="h-4 w-4" data-id="element-9204" data-mp="2zyhe64qmqd4s" />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start" data-id="element-9205" data-mp="2v17nymvf1xfw">
            <div className="flex items-center h-5" data-id="element-9206" data-mp="1ahvjzy33s6ja">
              <input id="acceptTerms" name="acceptTerms" type="checkbox" required checked={formData.acceptTerms} onChange={handleChange} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" data-id="element-9207" data-mp="1ffx32j0j45ur" />
            </div>
            <div className="ml-3 text-sm" data-id="element-9208" data-mp="1u4a0lkf9lwrd">
              <label htmlFor="acceptTerms" className={darkMode ? 'text-gray-300' : 'text-gray-600'} data-id="element-9209" data-mp="1y651intaa84m">
                I agree to the{' '}
                <Link to="/terms" className="font-medium text-purple-600 hover:text-purple-500" data-id="element-9210" data-mp="233oteqiwmjf1">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="font-medium text-purple-600 hover:text-purple-500" data-id="element-9211" data-mp="dps1b3ygncrz">
                  Privacy Policy
                </Link>
              </label>
            </div>
          </div>

          <Button type="submit" disabled={isLoading} className={`w-full py-2.5 text-base font-medium bg-purple-600 hover:bg-purple-700 focus:ring-purple-500 ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-9212" data-mp="1qmvattsrik9">
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </Button>

          <div className="mt-6 text-center" data-id="element-9213" data-mp="km3cd7vjkxzb">
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-9214" data-mp="klbpclg0g8kn">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-purple-600 hover:text-purple-500" data-id="element-9215" data-mp="tgreip5tlzjw">
                Sign in
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>;
};
export default CreateVendorAccount;